"use client";

import { useEffect, useState } from "react";
import Category from "./search-category";
import SearchList from "./search-list";
import { useSearchParams } from "next/navigation";
import useSWR from "swr";

import { getIndex, SearchConfig } from "@/server/meilisearch";
import { SearchResponse } from "meilisearch";

export interface Filter {
  value: string;
  label: string[];
  key: "news" | "application" | "product" | "techDoc" | "appNote" | "evmGuide";
  type: "news" | "application" | "product" | "techDoc";
  level?: number;
}

export interface SearchData {
  appNote?: string;
  newsCategory?: string;
  productCategory1?: string;
  productCategory2?: string;
  productCategory3?: string;
  applicationCategory1?: string;
  applicationCategory2?: string;
  content?: string;
  evmGuide?: string;
  product?: string;
  techDoc1?: string;
  techDoc2?: string;
  techDoc3?: string;
  title?: string;
  name?: string;
  publishDate?: number;
  type: "news" | "application" | "product";
  url?: string;
  id: string;
}

const generateFilter = (filter: Filter[]) => {
  const product = filter.find((i) => i.key === "product");

  const application = filter.find((i) => i.key === "application");

  const news = filter.find((i) => i.key === "news");

  const techDoc = filter.find((i) => i.key === "techDoc");

  const appNote = filter.find((i) => i.key === "appNote");

  const evmGuide = filter.find((i) => i.key === "evmGuide");

  if (news) {
    return ["type = 'news'", `newsCategory = '${news.value}'`];
  }

  const filterArray = [];

  if (product?.level === 1 || application?.level === 1) {
    filterArray.push(["type = 'application'", "type = 'product'"]);

    if (product?.level === 1) {
      filterArray.push(`productCategory1 = '${product.value}'`);
    }

    if (application?.level === 1) {
      filterArray.push(`applicationCategory1 = '${application.value}'`);
    }
  }

  if (product?.level && product.level > 1) {
    filterArray.push("type = 'product'");
    filterArray.push(`productCategory${product.level} = '${product.value}'`);
  }

  const techDocArray = [];

  if (appNote) {
    techDocArray.push("appNote EXISTS");
  }

  if (evmGuide) {
    techDocArray.push("evmGuide EXISTS");
  }

  if (techDoc) {
    techDocArray.push(`techDoc${techDoc.level} EXISTS`);
  }

  if (techDocArray.length > 0) {
    filterArray.push(techDocArray);
  }

  if (application?.level === 2) {
    filterArray.push("type = 'application'");
    filterArray.push(`applicationCategory2 = '${application.value}'`);
  }

  return filterArray;
};

// 特殊字符
export const specialChar = [
  "-",
  "_",
  "&",
  "!",
  "(",
  ")",
  "{",
  "}",
  "[",
  "]",
  "*",
  "?",
  ":",
  "#",
  "%",
  "/",
  ".",
  ",",
];

export default function SearchContent(props: {
  site: number;
  lang: number;
  searchConfig: SearchConfig;
}) {
  const keyword = useSearchParams().get("keyword");

  const [page, setPage] = useState(1);

  const [filter, setFilter] = useState<Filter[]>([]);

  const [sort, setSort] = useState<"correlation" | "date">("correlation");

  const [category, setCategory] = useState<
    "all" | "product" | "application" | "news"
  >("all");

  const [isMobile, setIsMobile] = useState<boolean>();

  const { data, isLoading } = useSWR(
    () =>
      !isMobile
        ? [`/api/search`, props.site, props.lang, keyword, page, filter, sort]
        : null,
    async () => {
      const client = getIndex(props.searchConfig, props.site);

      const filterArray = generateFilter(filter);

      // 过滤特殊字符
      const keywords = keyword
        ?.split("")
        ?.filter((i) => !specialChar.includes(i))
        ?.join("");

      if (!keywords?.length && keyword?.length) {
        return Promise.resolve({
          totalHits: 0,
          hits: [],
          processingTimeMs: 0,
          query: "",
        });
      }

      const searchData = (await client.search<SearchData>(keywords || "", {
        page: page,
        hitsPerPage: 10,
        filter: filterArray,
        sort: sort === "date" ? ["publishDate:desc"] : ["type:desc"],
      })) as SearchResponse<SearchData>;

      return searchData;
    }
  );

  useEffect(() => {
    const listener = () => {
      setIsMobile(window.matchMedia("(max-width: 768px)").matches);
    };
    listener();
    window.addEventListener("resize", listener);
    return () => {
      window.removeEventListener("resize", listener);
    };
  }, []);

  useEffect(() => {
    setPage(1);
  }, [keyword, filter]);

  return (
    <div className="flex flex-col lg:flex-row lg:gap-20 xl:gap-[7.5rem]">
      <Category
        site={props.site}
        lang={props.lang}
        keyword={keyword || ""}
        total={data?.totalHits ?? 0}
        filter={filter}
        setFilter={setFilter}
        sort={sort}
        setSort={setSort}
        searchConfig={props.searchConfig}
      />

      <SearchList
        filter={filter}
        setFilter={setFilter}
        data={data}
        site={props.site}
        lang={props.lang}
        keyword={keyword || ""}
        sort={sort}
        isMobile={isMobile}
        category={category}
        setCategory={setCategory}
        page={page}
        setPage={setPage}
        isLoading={isLoading}
        searchConfig={props.searchConfig}
      />
    </div>
  );
}
